// Private buying-power + feasibility math. Runs ONLY in the browser on the
// user's decrypted profile. Encodes Singapore financing rules as documented,
// configurable assumptions. This is decision *support*, not financial advice.

import { yearsHeldKnown, type Profile } from "../profile/schema";
import { hdbLeaseFinance } from "./leaseDecay";
import type { LeaseFinance } from "./leaseDecay";
import { absdRate, at, bsd, LOANS, SSD } from "../policy/policy";
import {
  bankRate, loanOptions, maxFundable, saleProceeds,
  type Buyer, type Funding, type Holding, type Lender, type SaleResult,
} from "../finance/costs";

// --- Rule assumptions ------------------------------------------------------
// Policy numbers (stamp duties, TDSR/MSR, LTV, stress floors) come from the
// dated, sourced registry in ../policy/policy.ts, resolved for today. The
// remaining fields are app assumptions, not policy, and are labelled as such.
const loans = at(LOANS).value;
export const RULES = {
  stressRateAnnual: loans.bankStressRate, // MAS medium-term rate floor, bank loans (policy.ts)
  hdbStressRate: loans.hdbStressRate, // HDB loan eligibility floor (policy.ts)
  tdsr: loans.tdsr,
  msr: loans.msr, // HDB/EC only
  ltv: loans.bankLtvFirst, // bank loan, no outstanding housing loan
  hdbLoanLtv: loans.hdbLoanLtv,
  hdbTenureMax: loans.ltvTierTenureHdb, // longer tenure drops to the lower LTV tier (MAS, policy.ts)
  privateTenureMax: loans.ltvTierTenureOther, // ditto for non-HDB
  loanAgeCeiling: loans.ltvTierAge, // loan running past this age drops to the lower LTV tier
  absdSecondProperty: absdRate("SC", 2), // SC, 2nd property (policy.ts); use absdRate() for others
  ltvSecondLoan: loans.bankLtvSecond,
  sellingCostRate: 0.02, // assumption: agent commission + legal on a sale
  ssdRatesByYear: at(SSD).value, // for purchases made today; use ssdRate() for a past purchase
  hdbMopYears: 5, // minimum occupation period before an HDB flat can be sold
  privateToHdbWaitMonths: 15, // wait-out after disposing private before buying HDB (non-subsidised)
  effectiveFrom: at(LOANS).effectiveFrom, // latest loan-rule change in force
  sources: "web/src/policy/policy.ts",
  car: {
    ltv: 0.6, // max financing (OMV > $20k tier)
    tenureYears: 7,
    flatRateAnnual: 0.03, // typical car-loan flat rate
    prudentIncomeShare: 0.15, // comfort line for car costs as share of gross income
  },
};

// IRAS Buyer's Stamp Duty (residential), marginal tiers in force on `date`.
export function buyerStampDuty(price: number, date?: string): number {
  return bsd(price, date);
}

// Present value of an annuity: max loan supportable by a monthly payment.
export function annuityPV(monthly: number, annualRate: number, years: number): number {
  const r = annualRate / 12;
  const n = years * 12;
  if (monthly <= 0 || n <= 0) return 0;
  if (r === 0) return monthly * n;
  return (monthly * (1 - Math.pow(1 + r, -n))) / r;
}

export function loanTenure(age: number, max: number): number {
  return Math.max(5, Math.min(max, RULES.loanAgeCeiling - age));
}

export interface PropertyBuyingPower {
  maxPrice: number;
  boundedBy: "loan" | "cash";
  maxLoan: number;
  monthlyRepayment: number; // at stress rate
  cashCpfNeeded: number; // downpayment + stamp duties at maxPrice
  availableFunds: number;
  freeCashFromProperty: number; // equity unlocked if current property is sold
  monthlyAtMarketRate: number; // estimated actual repayment at the user's rate
  tenureYears: number;
  lease?: LeaseFinance; // HDB only, when a target remaining lease is set
  lender: Lender; // lender behind maxPrice (the best option)
  options: LenderOption[]; // every loan option, for the side-by-side view
  sale?: SaleResult; // when selling the current home to upgrade
  rateSource: "manual" | "sora" | "default"; // where the bank rate came from
}

export interface LenderOption {
  label: string;
  lender: Lender;
  maxPrice: number;
  funding: Funding; // at maxPrice
  note: string;
  eligible: boolean;
}

/** The buyer as the ABSD / loan rules see them. */
export function buyerOf(p: Profile): Buyer {
  return { residency: p.residency ?? "SC", married: p.married === true, spouseResidency: p.spouseResidency, age: p.ageYears };
}

/** The currently-owned home as a sale input. */
export function holdingOf(p: Profile): Holding {
  return {
    value: Math.max(0, p.currentPropertyValue ?? 0),
    outstandingLoan: Math.max(0, p.currentPropertyOutstandingLoan ?? 0),
    yearsHeld: Math.max(0, p.currentPropertyYearsHeld ?? 0),
    yearsHeldKnown: yearsHeldKnown(p),
    isHdbFlat: (p.currentPropertyType ?? "").startsWith("hdb"),
    cpfRefundOverride: p.cpfUsedInCurrent > 0 ? p.cpfUsedInCurrent : undefined,
    cpfLumpSum: p.cpfLumpSumUsed,
    cpfMonthly: p.cpfMonthlyInstalment,
  };
}

/** Whether the user typed their own mortgage rate (else SORA + spread). */
export function rateIsManual(p: Profile): boolean {
  return p.mortgageRateManual ?? (p.mortgageRatePct != null && p.mortgageRatePct !== 3.5);
}

// Monthly payment to fully amortise `principal` at `annualRate` over `years`.
export function annuityPayment(principal: number, annualRate: number, years: number): number {
  const r = annualRate / 12;
  const n = years * 12;
  if (principal <= 0 || n <= 0) return 0;
  if (r === 0) return principal / n;
  return (principal * r) / (1 - Math.pow(1 + r, -n));
}

/** Estimated equity in the currently-owned property (value less loan). */
export function propertyEquity(p: Profile): number {
  return Math.max(0, p.currentPropertyValue - p.currentPropertyOutstandingLoan);
}

/**
 * Max property price the user can support under each loan option (HDB loan and
 * bank loan tiers for an HDB flat; bank tiers for private), funding the
 * downpayment, minimum cash, duties and fees from cash and CPF separately.
 * maxPrice is the best option; `options` carries all of them.
 */
export function propertyBuyingPower(p: Profile, isHdb: boolean, ctx: { sora?: number | null } = {}): PropertyBuyingPower {
  // Whether you own now is derived from the property value, not a separate flag.
  const owns = p.currentPropertyValue > 0;
  // Intent when you already own: "second" = keep the current home and buy an
  // additional property (ABSD + tighter 2nd-loan LTV, existing mortgage still
  // counts). Otherwise "upgrade" = sell the current home first, so the cash and
  // CPF refund from the sale fund the next one (no ABSD, mortgage discharged).
  const keepCurrent = owns && p.buyIntent === "second";
  const sellingToUpgrade = owns && !keepCurrent;
  const useAgent = p.useAgent !== false;
  const sale = sellingToUpgrade ? saleProceeds(holdingOf(p), useAgent) : undefined;

  // HDB lease decay: CPF usage, LTV and tenure pro-rated/capped when the lease
  // doesn't cover the youngest buyer to 95 (lease-decay engine).
  const lease: LeaseFinance | undefined =
    isHdb && (p.targetHdbRemainingLease ?? 0) > 0
      ? hdbLeaseFinance(p.targetHdbRemainingLease as number, p.ageYears)
      : undefined;

  const rate = bankRate(p.mortgageRatePct, rateIsManual(p), ctx.sora);
  const terms = loanOptions({
    isHdbFlat: isHdb,
    age: p.ageYears,
    outstandingHousingLoans: keepCurrent && (p.existingHomeLoanMonthly ?? 0) > 0 ? 1 : 0,
    bankRate: rate.rate,
    leaseLtvFactor: lease?.ltvFactor,
    leaseTenureCap: lease ? lease.maxTenureCap : undefined,
    remainingLease: isHdb ? p.targetHdbRemainingLease || undefined : undefined,
    householdIncome: p.monthlyGrossIncome,
    hasCitizen: (p.residency ?? "SC") === "SC" || (p.married === true && p.spouseResidency === "SC"),
  });
  const funds = {
    cash: (p.cashSavings ?? 0) + (p.otherLiquidAssets ?? 0) + (sale?.cash ?? 0),
    cpf: (p.cpfOaBalance ?? 0) + (sale?.cpfRefund ?? 0),
  };
  const ob = {
    income: p.monthlyGrossIncome,
    otherDebt: p.monthlyDebtRepayments ?? 0,
    housingDebt: keepCurrent ? p.existingHomeLoanMonthly ?? 0 : 0,
  };
  const base = {
    isHdbFlat: isHdb, buyer: buyerOf(p), nth: keepCurrent ? 2 : 1, sellingFirst: false, useAgent,
    cpfLeaseFraction: lease ? lease.cpfFraction : undefined,
  };
  const options: LenderOption[] = terms.map((t) => {
    const f = maxFundable(base, t, funds, ob);
    return { label: t.label, lender: t.lender, maxPrice: f.price, funding: f, note: t.note, eligible: t.eligible };
  });
  // Buying power = the best option you're eligible for; ineligible ones stay in
  // `options` so the side-by-side shows why they're out.
  const best = options.filter((o) => o.eligible).reduce<LenderOption | null>((b, o) => (!b || o.maxPrice > b.maxPrice ? o : b), null);
  const f = best?.funding;

  return {
    maxPrice: best?.maxPrice ?? 0,
    boundedBy: f && f.boundedBy === "income" ? "loan" : "cash",
    maxLoan: f?.loan ?? 0,
    monthlyRepayment: f?.monthlyStress ?? 0,
    cashCpfNeeded: f ? f.cashNeeded + f.cpfNeeded : 0,
    availableFunds: funds.cash + funds.cpf,
    freeCashFromProperty: sale ? sale.cash + sale.cpfRefund : 0,
    monthlyAtMarketRate: f?.monthlyMarket ?? 0,
    tenureYears: f?.terms.tenureYears ?? 0,
    lease,
    lender: best?.lender ?? "bank",
    options,
    sale,
    rateSource: rate.source,
  };
}

export interface CarBuyingPower {
  maxPrice: number;
  boundedBy: "cash" | "income";
  downpayment: number; // at maxPrice
  monthlyInstalment: number; // at maxPrice
  incomeCeilingMonthly: number;
  freeCashFromCar: number; // equity freed by selling the current car
}

/** Estimated equity in the currently-owned car (value less loan). */
export function carEquity(p: Profile): number {
  return Math.max(0, (p.currentCarValue ?? 0) - (p.currentCarOutstandingLoan ?? 0));
}

/** Max car price supportable, lesser of cash (40% down) and income (instalment). */
export function carBuyingPower(p: Profile): CarBuyingPower {
  const { ltv, tenureYears } = RULES.car;
  const flatRate = (p.carLoanRatePct ?? RULES.car.flatRateAnnual * 100) / 100;
  const income = p.monthlyGrossIncome;
  const room = Math.max(
    0,
    RULES.tdsr * income - p.monthlyDebtRepayments - p.existingHomeLoanMonthly,
  );

  // flat-rate instalment factor per dollar of loan
  const instalmentPerLoanDollar = (1 + flatRate * tenureYears) / (tenureYears * 12);

  // Selling the current car frees its equity as cash toward the next one.
  const freeCashFromCar = carEquity(p);
  const priceByIncome = room / instalmentPerLoanDollar / ltv;
  const priceByCash = (p.cashSavings + freeCashFromCar) / (1 - ltv);

  const maxPrice = Math.max(0, Math.min(priceByIncome, priceByCash));
  const boundedBy = priceByIncome <= priceByCash ? "income" : "cash";

  return {
    maxPrice,
    boundedBy,
    downpayment: maxPrice * (1 - ltv),
    monthlyInstalment: maxPrice * ltv * instalmentPerLoanDollar,
    incomeCeilingMonthly: room,
    freeCashFromCar,
  };
}

// --- Feasibility: blend affordability with market timing -------------------
export type FeasibilityBand = "buy-ready" | "reachable" | "stretch" | "out-of-reach";

export interface Feasibility {
  affordabilityRatio: number; // buyingPower / marketPrice (>=1 => affordable)
  affordabilityBand: FeasibilityBand;
  entryScore: number | null; // 0..100 market timing from server (higher=better)
  overallScore: number; // 0..100 combined
  recommendation: string;
}

export function feasibility(
  buyingPower: number,
  marketPrice: number,
  entryScore: number | null,
): Feasibility {
  const ratio = marketPrice > 0 ? buyingPower / marketPrice : 0;

  const affordabilityBand: FeasibilityBand =
    ratio >= 1.1 ? "buy-ready" : ratio >= 1 ? "reachable" : ratio >= 0.85 ? "stretch" : "out-of-reach";

  // Affordability -> 0..100 (1.0x ratio = 70, 1.2x = 100, 0.7x = 0).
  const affScore = Math.max(0, Math.min(100, (ratio - 0.7) * 200));
  // Affordability dominates: no point timing a market you can't enter. With no
  // usable timing signal (none, or not predictive per the backtest rule) the
  // score is affordability alone.
  const overallScore = entryScore == null ? Math.round(affScore) : Math.round(0.65 * affScore + 0.35 * entryScore);

  let recommendation: string;
  if (affordabilityBand === "out-of-reach") {
    recommendation =
      "Above your buying power under current loan rules.";
  } else if (entryScore == null) {
    // No usable timing signal (none, or not predictive per the backtest rule).
    recommendation = affordabilityBand === "stretch"
      ? "A stretch at current prices — a bigger downpayment, a smaller unit or another area would fit better."
      : "Within your budget. Decide on the specific unit and its price against recent sales — not on market timing.";
  } else if (affordabilityBand === "stretch") {
    recommendation =
      entryScore >= 62
        ? "A stretch on your budget; prices look soft against trend (tentative timing read)."
        : "A stretch on your budget; prices aren't especially soft.";
  } else {
    recommendation =
      entryScore >= 62
        ? "Within your budget; prices look soft against trend (tentative timing read)."
        : entryScore <= 40
          ? "Within your budget; prices look stretched against trend (tentative timing read)."
          : "Within your budget; timing is neutral.";
  }

  return { affordabilityRatio: ratio, affordabilityBand, entryScore, overallScore, recommendation };
}
